import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { AutocompleteList, SearchField } from '@aok/ui-elements'

import { decodeHtmlToText } from '../../../utils/TextUtils'

/**
 * Search component with autocomplete
 * @returns
 */
const SearchAutocomplete = ({
  autocompleteProps,
  className,
  initialSearchString,
  minCharsToAutoComplete,
  isOverlay,
  onSubmit,
  onSelect,
  onRequestAutocompleteData,
  onInputChange,
  onDeleteButtonClick,
  searchFieldProps,
  setIsAutocompleteOpen,
}) => {
  const autocompleteId = `${searchFieldProps?.id}-autocomplete-list`

  const autocompleteFormRef = useRef(null)
  const autocompleteRef = useRef(null)
  const searchFieldInputRef = useRef(null)

  const [validChoiceMade, setValidChoiceMade] = useState(false)
  const [searchString, setSearchString] = useState(initialSearchString || '')
  const [autocompleteIsVisible, setAutocompleteIsVisible] = useState(false)
  const [autocompleteShouldReceiveFocus, setAutocompleteShouldReceiveFocus] = useState(false)

  const [searchFieldSubmitted, setSearchFieldSubmitted] = useState(false)
  const [autocompleteList, setAutocompleteList] = useState([])

  const hideAutocompleteList = () => {
    setAutocompleteList([])
    setAutocompleteShouldReceiveFocus(false)
  }

  const handleInputFocus = (e) => {
    if (!validChoiceMade && e.target.value && e.target.value.trim().length >= minCharsToAutoComplete) {
      onRequestAutocompleteData(e.target.value, (list) => {
        setAutocompleteList(list)
      })
    }
  }

  const handleInputChange = (e) => {
    setSearchString(e.target.value)
    setSearchFieldSubmitted(false)

    if (e.target.value && e.target.value.trim().length >= minCharsToAutoComplete) {
      onRequestAutocompleteData(e.target.value, (list) => {
        setAutocompleteList(list)
      })
    } else {
      hideAutocompleteList()
    }

    onInputChange(e)
  }

  const handleSelectAutocompleteEntry = (_e, selectedEntry) => {
    const decodedEntry = decodeHtmlToText(selectedEntry) // entry may contain HTML, e.g. <em> tags

    setSearchString(decodedEntry)
    setValidChoiceMade(true)
    hideAutocompleteList()

    onSelect(decodedEntry)
  }

  const handleSubmitButtonClick = () => {
    hideAutocompleteList()
    setSearchFieldSubmitted(true)
    onSubmit(searchString)
  }

  const handleDeleteButtonClick = () => {
    setSearchString('')
    onDeleteButtonClick()
  }

  const handleInputKeyUp = (e) => {
    // if arrow down key is pressed
    if (e.keyCode === 40) {
      if (e.target.value && e.target.value.trim().length >= minCharsToAutoComplete) {
        onRequestAutocompleteData(e.target.value, (list) => {
          setAutocompleteList(list)
          setAutocompleteIsVisible(true)
          setIsAutocompleteOpen(true)
          setAutocompleteShouldReceiveFocus(true)
        })
      }
    }
  }

  const handleInputKeyDown = (e) => {
    // if tab key is pressed
    if (e.keyCode === 9) {
      if (autocompleteIsVisible) {
        hideAutocompleteList()
      }
    }
  }

  const handleShouldHideAutocompleteList = () => {
    hideAutocompleteList()
    setValidChoiceMade(true)
  }

  useEffect(() => {
    setSearchString(initialSearchString)
  }, [initialSearchString])

  useEffect(() => {
    if (validChoiceMade) {
      searchFieldInputRef?.current?.focus()
      setValidChoiceMade(false)
    }
  }, [validChoiceMade])

  useEffect(() => {
    if (autocompleteList.length && !searchFieldSubmitted && searchString?.trim()?.length >= minCharsToAutoComplete) {
      setAutocompleteIsVisible(true)
      setIsAutocompleteOpen(true)
    } else {
      setAutocompleteIsVisible(false)
      setIsAutocompleteOpen(false)
    }
  }, [autocompleteList, setIsAutocompleteOpen, searchFieldSubmitted, searchString, minCharsToAutoComplete])

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!autocompleteFormRef?.current?.contains(e.target)) {
        hideAutocompleteList()
      }
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [])

  return (
    <form
      className={`relative ${className}`}
      autoComplete="off"
      noValidate
      onSubmit={(e) => e.preventDefault()}
      ref={autocompleteFormRef}
    >
      <SearchField
        {...searchFieldProps}
        ref={searchFieldInputRef}
        onFocusInput={handleInputFocus}
        onChangeInput={handleInputChange}
        onKeyUpInput={handleInputKeyUp}
        onKeyDownInput={handleInputKeyDown}
        onSubmitButtonClick={handleSubmitButtonClick}
        onDeleteButtonClick={handleDeleteButtonClick}
        inputProps={{
          'type': 'text',
          'name': 'q',
          'value': searchString,
          'aria-owns': autocompleteId,
          'autoCapitalize': 'none',
          'autcomplete': 'off',
          'aria-autocomplete': 'list',
          'aria-expanded': autocompleteIsVisible,
          'role': 'combobox',
          ...searchFieldProps?.inputProps,
        }}
      />

      {autocompleteIsVisible && (
        <div
          className={`
            searchAutocompleteContainer
            overflow-y-auto rounded-4
            ${
              isOverlay
                ? 'absolute top-full left-0 right-0 mt-8 z-20 shadow-autocompleteBox max-h-350 840:max-h-494'
                : ''
            }

          `}
        >
          <AutocompleteList
            id={autocompleteId}
            ref={autocompleteRef}
            list={autocompleteList}
            ariaLabel={searchFieldProps?.label}
            onSelect={handleSelectAutocompleteEntry}
            onShouldHideAutocompleteList={handleShouldHideAutocompleteList}
            shouldReceiveFocus={autocompleteShouldReceiveFocus}
            className="h-full"
            {...autocompleteProps}
          />
        </div>
      )}
    </form>
  )
}

SearchAutocomplete.propTypes = {
  autocompleteProps: PropTypes.object,
  className: PropTypes.string,
  initialSearchString: PropTypes.string,
  minCharsToAutoComplete: PropTypes.number,
  isOverlay: PropTypes.bool,
  onSubmit: PropTypes.func,
  onSelect: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
  searchFieldProps: PropTypes.object,
  setIsAutocompleteOpen: PropTypes.func,
}

SearchAutocomplete.defaultProps = {
  autocompleteProps: {
    variant: 'dark',
  },
  className: '',
  initialSearchString: '',
  minCharsToAutoComplete: 3,
  isOverlay: true,
  onSubmit: () => {},
  onSelect: () => {},
  onInputChange: () => {},
  onDeleteButtonClick: () => {},
  setIsAutocompleteOpen: () => {},
}

export default SearchAutocomplete
